import { Button, HStack, Spacer, useToast } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { MealPlanView } from '@web-app/components/CreateMealPlan/utils';
import { useMealPlanWizard } from '@web-app/hooks/useMealPlanWizard';
import { isProd } from '@web-app/utils/env.utils';
import { useMolecule } from 'bunshi/react';
import { useSetAtom } from 'jotai';
import { FC, useCallback, useEffect, useState } from 'react';

const actionButtonLabel: Record<MealPlanView, string> = {
  [MealPlanView.Instructions]: 'Next: Review recipe outline',
  [MealPlanView.Summary]: 'Next: Review recipes',
  [MealPlanView.Details]: 'Send to patient',
};

export interface MealPlanWizardFooterProps {
  dailyRoomName: string;
  onMealPlanSent?: () => void;
  onClose?: () => void;
  onReadOnlyChange?: (isReadOnly: boolean) => void;
  usePreviousAppointment?: boolean;
}

/**
 * Shared wizard footer for the meal plan drawer and standalone page.
 * Must be rendered inside a MealPlanScope ScopeProvider.
 *
 * Renders: Back | Reset (dev only) | [spacer] | Close (optional) | Action button
 * Hides the action button when the meal plan has already been sent (read-only).
 */
export const MealPlanWizardFooter: FC<MealPlanWizardFooterProps> = ({
  dailyRoomName,
  onMealPlanSent,
  onClose,
  onReadOnlyChange,
  usePreviousAppointment = false,
}) => {
  const toast = useToast({
    position: 'bottom-left',
    isClosable: true,
  });

  const { refetchMealPlanAtom } = useMolecule(mealPlanMolecule);
  const refetchMealPlan = useSetAtom(refetchMealPlanAtom);

  const {
    activeMealPlanStep,
    performMealPlanAction,
    isMealPlanBusy,
    isAnyMealPlanLoading,
    mealPlanInstructions,
    setActiveMealPlanStep,
    detailedMealPlan,
  } = useMealPlanWizard({
    onMealPlanSent: () => {
      onMealPlanSent?.();
    },
    usePreviousAppointment,
  });

  const [isResetting, setIsResetting] = useState(false);

  const isReadOnly = !!detailedMealPlan?.isSent;

  useEffect(
    function notifyReadOnlyChange() {
      onReadOnlyChange?.(isReadOnly);
    },
    [isReadOnly, onReadOnlyChange],
  );

  const handleReset = useCallback(async () => {
    setIsResetting(true);
    try {
      const res = await fetch(`/api/meal-plan/${dailyRoomName}/reset`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Reset failed');

      await refetchMealPlan();
      setActiveMealPlanStep(MealPlanView.Instructions);

      toast({
        title: 'Meal plan reset.',
        status: 'info',
        duration: 3000,
      });
    } catch {
      toast({
        title: 'Failed to reset meal plan.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsResetting(false);
    }
  }, [dailyRoomName, refetchMealPlan, setActiveMealPlanStep, toast]);

  const handleBack = () => {
    if (activeMealPlanStep === MealPlanView.Summary) {
      setActiveMealPlanStep(MealPlanView.Instructions);
    } else if (activeMealPlanStep === MealPlanView.Details) {
      setActiveMealPlanStep(MealPlanView.Summary);
    }
  };

  const isActionDisabled =
    isMealPlanBusy ||
    isAnyMealPlanLoading ||
    (activeMealPlanStep === MealPlanView.Instructions && !mealPlanInstructions);

  return (
    <HStack spacing={3} w="full">
      {activeMealPlanStep !== MealPlanView.Instructions && (
        <Button
          variant="ghost"
          onClick={handleBack}
          isDisabled={isMealPlanBusy || isResetting}
        >
          Back
        </Button>
      )}
      {!isProd && (
        <Button
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={handleReset}
          isLoading={isResetting}
          isDisabled={isMealPlanBusy}
        >
          Reset
        </Button>
      )}
      <Spacer />
      {onClose && (
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      )}
      {!isReadOnly && (
        <Button
          bg="interactive.brand.default"
          color="text.inverse"
          _hover={{ bg: 'interactive.brand.hover' }}
          _active={{ bg: 'interactive.brand.active' }}
          onClick={performMealPlanAction}
          isLoading={isMealPlanBusy}
          isDisabled={isActionDisabled}
        >
          {actionButtonLabel[activeMealPlanStep]}
        </Button>
      )}
    </HStack>
  );
};
